import { useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2 } from 'lucide-react'

/**
 * Blocks the first paint until i18next has finished loading the initial
 * translation bundle from the API.
 *
 * react-i18next is configured with `useSuspense: false` (see
 * `frontend/src/admin/lib/i18n.ts`), so before the async `/translations/`
 * fetch resolves every `t('some.key')` renders the raw key. Without this gate
 * the app briefly paints untranslated keys - a visible flash of translation
 * strings on load.
 *
 * The gate is deliberately sticky: once translations have been ready once,
 * children stay mounted. A later `i18n.changeLanguage()` (e.g. `useOrg`
 * syncing to the organisation's preferred language after login) must not blank
 * the whole app - i18next keeps serving the previous language's strings while
 * the new bundle loads, which is far better UX than a full-screen loader
 * mid-session.
 *
 * If the translations API is unavailable the backend resolves with an empty
 * bundle (graceful fallback in `i18n.ts`), so `ready` still flips to true and
 * the app renders with keys falling back to themselves - never an infinite
 * loader.
 */
export function I18nGate({ children }: { children: ReactNode }) {
  const { ready } = useTranslation()
  const [hasLoaded, setHasLoaded] = useState(false)

  useEffect(() => {
    if (ready) setHasLoaded(true)
  }, [ready])

  if (!ready && !hasLoaded) {
    return (
      <div className="flex items-center justify-center h-screen bg-[var(--bg)]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return <>{children}</>
}
